import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CdkOverlayOrigin } from '@angular/cdk/overlay';
import { equals } from '@softline/core';
import { DateParser } from '@softline/ui-core';
import { SOFTLINE_SERVICE_APPEARANCE } from '@softline/ui-core';
import { DateRange } from '@softline/ui-core';
import { TranslatePipe } from '@softline/ui-core';
import { DateRangePickerShortcut } from '@softline/ui-core/date-input';
import { OverlayComponent } from '@softline/ui-core';
import { PrevCurrNextNavigationComponent } from '../prev-curr-next-navigation/prev-curr-next-navigation.component';
import { IconComponent } from '@softline/ui-core';
import { DateRangeDropdownVariant } from './date-range-dropdown.types';
import { DateRangeDropdownMobiscrollHostComponent } from './date-range-dropdown-mobiscroll-host.component';
import { LocalizationService } from '@softline/ui-core';

interface DraftRange {
  from: string | null;
  to: string | null;
}

type DateRangeDropdownTranslation =
  | string
  | { message: string; params: Record<string, unknown> };

interface DateRangeDropdownShortcut extends DateRangePickerShortcut {
  readonly id: string;
  readonly labelParams?: Record<string, unknown>;
}

const DATE_RANGE_DROPDOWN_I18N = '#UI_CORE.COMPONENTS.DATE_RANGE_DROPDOWN';

@Component({
  selector: 'soft-ds-date-range-dropdown',
  templateUrl: './date-range-dropdown.component.html',
  styleUrl: './date-range-dropdown.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: { '[class]': 'hostClasses()' },
  imports: [
    FormsModule,
    CdkOverlayOrigin,
    OverlayComponent,
    PrevCurrNextNavigationComponent,
    IconComponent,
    TranslatePipe,
    DateRangeDropdownMobiscrollHostComponent,
  ],
})
export class DateRangeDropdownComponent {
  protected readonly appearanceService = inject(SOFTLINE_SERVICE_APPEARANCE);
  private readonly dateParser = inject<DateParser<string>>(DateParser);
  private readonly localizationService = inject(LocalizationService);

  readonly value = model<DateRange | null>(null);
  readonly open = model(false);
  readonly variant = input<DateRangeDropdownVariant>('plain');
  readonly size = input<'xs' | 'sm' | 'md' | 'lg' | 'xl'>('md');
  readonly shortcuts = input<readonly DateRangePickerShortcut[] | null>(null);
  readonly totalRange = input<DateRange | null>(null);
  readonly placeholder = input<string>(
    '#UI_CORE.COMPONENTS.DATE_RANGE_DROPDOWN.PLACEHOLDER'
  );
  readonly showShortcuts = input(true);
  readonly showBuiltinShortcuts = input(true);
  readonly showWeekNumbers = input(false);
  readonly disabled = input(false);
  readonly confirm = output<DateRange>();

  protected readonly draftValue = signal<DraftRange | null>(null);
  protected readonly displayFromInput = signal<string | null>(null);
  protected readonly displayToInput = signal<string | null>(null);
  protected readonly displayFrom = computed(
    () =>
      this.displayFromInput() ??
      this.formatFullDate(this.draftValue()?.from ?? null)
  );
  protected readonly displayTo = computed(
    () =>
      this.displayToInput() ??
      this.formatFullDate(this.draftValue()?.to ?? null)
  );
  protected readonly refDate = signal<Date>(new Date());
  protected readonly activeShortcut = signal<string | null>(null);

  private readonly _now = new Date();
  private readonly _currentYear = this._now.getFullYear();
  private readonly _currentMonth = this._now.getMonth() + 1;

  protected readonly builtinShortcuts = computed(
    (): readonly DateRangeDropdownShortcut[] => {
      if (!this.showBuiltinShortcuts()) return [];
      const y = this._currentYear;
      const m = this._currentMonth;
      const list: DateRangeDropdownShortcut[] = [];

      const totalFrom = this.totalRange()?.from ?? '1970-01-01';
      list.push({
        id: 'all',
        label: `${DATE_RANGE_DROPDOWN_I18N}.ALL_TIME`,
        value: { from: totalFrom, to: this.toIsoDate(this._now) },
      });

      list.push({
        id: 'this-year',
        label: `${DATE_RANGE_DROPDOWN_I18N}.THIS_YEAR`,
        labelParams: { year: y },
        value: { from: `${y}-01-01`, to: `${y}-12-31` },
      });
      if (m >= 1)
        list.push({
          id: 'quarter-1',
          label: `${DATE_RANGE_DROPDOWN_I18N}.QUARTER_1`,
          value: { from: `${y}-01-01`, to: `${y}-03-31` },
        });
      if (m >= 4)
        list.push({
          id: 'quarter-2',
          label: `${DATE_RANGE_DROPDOWN_I18N}.QUARTER_2`,
          value: { from: `${y}-04-01`, to: `${y}-06-30` },
        });
      if (m >= 7)
        list.push({
          id: 'quarter-3',
          label: `${DATE_RANGE_DROPDOWN_I18N}.QUARTER_3`,
          value: { from: `${y}-07-01`, to: `${y}-09-30` },
        });
      if (m >= 10)
        list.push({
          id: 'quarter-4',
          label: `${DATE_RANGE_DROPDOWN_I18N}.QUARTER_4`,
          value: { from: `${y}-10-01`, to: `${y}-12-31` },
        });

      const locale = this.localizationService.locale();
      for (let i = 1; i <= m; i++) {
        const mm = String(i).padStart(2, '0');
        const lastDay = new Date(y, i, 0).getDate();
        const dd = String(lastDay).padStart(2, '0');
        const monthName = new Date(y, i - 1, 1).toLocaleDateString(locale, { month: 'long' });
        const label = `${monthName.charAt(0).toUpperCase()}${monthName.slice(1)} ${y}`;
        list.push({
          id: `month-${mm}`,
          label,
          value: { from: `${y}-${mm}-01`, to: `${y}-${mm}-${dd}` },
        });
      }

      return list;
    }
  );

  protected readonly customShortcuts = computed(
    (): readonly DateRangeDropdownShortcut[] =>
      (this.shortcuts() ?? []).map((shortcut, index) => ({
        ...shortcut,
        id: `custom-${index}-${shortcut.label}`,
      }))
  );

  protected readonly allShortcuts = computed((): readonly DateRangeDropdownShortcut[] => [
    ...this.builtinShortcuts(),
    ...this.customShortcuts(),
  ]);

  protected readonly hostClasses = computed(
    () => `soft-ds-date-range-dropdown-host variant-${this.variant()} size-${this.size()}`
  );

  private static readonly SIZE_HEIGHT: Record<string, number> = {
    xs: 24, sm: 28, md: 32, lg: 36, xl: 40,
  };

  protected readonly navBtnSize = computed(() => {
    const h = DateRangeDropdownComponent.SIZE_HEIGHT[this.size()] ?? 32;
    return this.variant() === 'outline' ? `${h - 2}px` : `${h}px`;
  });

  protected readonly triggerHeight = computed(() => {
    const h = DateRangeDropdownComponent.SIZE_HEIGHT[this.size()] ?? 32;
    return `${this.variant() === 'outline' ? h - 2 : h}px`;
  });

  protected readonly triggerLabel = computed(() => {
    const v = this.value();
    if (!v?.from || !v?.to) return null;
    return `${this.formatFullDate(v.from)} – ${this.formatFullDate(v.to)}`;
  });

  protected readonly dayNamesMin = computed(() => {
    const locale = this.localizationService.locale();
    const sunday = new Date(2026, 0, 4);
    return Array.from({ length: 7 }, (_, index) =>
      new Intl.DateTimeFormat(locale, { weekday: 'short' }).format(
        new Date(sunday.getFullYear(), sunday.getMonth(), sunday.getDate() + index)
      )
    );
  });

  protected readonly mbscValue = computed(() => {
    const v = this.draftValue();
    return v ? [v.from, v.to] : [null, null];
  });

  readonly themeVariant = computed(() => {
    const isDarkMode = this.appearanceService.isDarkMode?.();
    if (isDarkMode !== undefined) return isDarkMode ? 'dark' : 'light';
    if (this.appearanceService.systemDarkModeEnabled()) return 'dark';
    return 'light';
  });

  protected readonly _openEffect = effect(() => {
    if (this.open()) {
      const current = this.value();
      this.draftValue.set(current ? { from: current.from, to: current.to } : null);
      this.activeShortcut.set(null);
      this.clearDisplayInputs();
      this.refDate.set(new Date());
    }
  });

  protected onTriggerClick(): void {
    if (this.disabled()) return;
    this.open.update((o) => !o);
  }

  protected onPickerValueChange(valueArray: [string | null, string | null]): void {
    const [from, to] = valueArray;
    const next = from || to ? { from, to } : null;
    if (equals(this.draftValue(), next)) return;
    this.draftValue.set(next);
    this.activeShortcut.set(null);
    this.clearDisplayInputs();
  }

  protected onSelectShortcut(shortcut: DateRangeDropdownShortcut): void {
    const from = shortcut.value.from ? this.dateParser.parse(shortcut.value.from) : null;
    const to = shortcut.value.to ? this.dateParser.parse(shortcut.value.to) : null;
    if (!from || !to) return;
    this.draftValue.set({ from, to });
    this.activeShortcut.set(shortcut.id);
    this.clearDisplayInputs();
    this.refDate.set(new Date(from));
  }

  protected onConfirm(): void {
    const v = this.draftValue();
    if (!v?.from || !v?.to) return;
    const range: DateRange = { from: v.from, to: v.to };
    this.value.set(range);
    this.confirm.emit(range);
    this.open.set(false);
  }

  protected onDisplayFromBlur(raw: string): void {
    const parsed = this.dateParser.parse(raw.trim());
    if (parsed) {
      this.draftValue.update((v) => ({
        ...(v ?? { from: null, to: null }),
        from: parsed,
      }));
    }
    this.displayFromInput.set(null);
  }

  protected onSelectTotalRange(): void {
    const from = this.totalRange()?.from ?? '1970-01-01';
    this.draftValue.set({ from, to: this.toIsoDate(new Date()) });
    this.activeShortcut.set('all');
    this.clearDisplayInputs();
  }

  protected onDisplayToBlur(raw: string): void {
    const parsed = this.dateParser.parse(raw.trim());
    if (parsed) {
      this.draftValue.update((v) => ({ ...(v ?? { from: null, to: null }), to: parsed }));
    }
    this.displayToInput.set(null);
  }

  protected shortcutLabel(
    shortcut: DateRangeDropdownShortcut
  ): DateRangeDropdownTranslation {
    if (shortcut.labelParams) {
      return { message: shortcut.label, params: shortcut.labelParams };
    }
    return shortcut.label;
  }

  protected onTriggerPrev(): void {
    this.shiftValue(-1);
  }

  protected onTriggerNext(): void {
    this.shiftValue(1);
  }

  protected onTriggerToday(): void {
    const v = this.value();
    if (!v?.from || !v?.to) return;
    const from = new Date(v.from);
    const to = new Date(v.to);
    const durationMs = to.getTime() - from.getTime();
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const newTo = new Date(today.getTime() + durationMs);
    const range: DateRange = { from: this.toIsoDate(today), to: this.toIsoDate(newTo) };
    this.value.set(range);
    this.confirm.emit(range);
  }

  protected onPrevMonth(): void {
    this.refDate.update((d) => new Date(d.getFullYear(), d.getMonth() - 1, 1));
  }

  protected onNextMonth(): void {
    this.refDate.update((d) => new Date(d.getFullYear(), d.getMonth() + 1, 1));
  }

  protected onTodayNav(): void {
    this.refDate.set(new Date());
  }

  private shiftValue(direction: 1 | -1): void {
    const v = this.value();
    if (!v?.from || !v?.to) return;
    const from = new Date(v.from);
    const to = new Date(v.to);
    const durationMs = to.getTime() - from.getTime();
    const shiftMs = (durationMs + 86_400_000) * direction;
    const newFrom = new Date(from.getTime() + shiftMs);
    const newTo = new Date(to.getTime() + shiftMs);
    const range: DateRange = { from: this.toIsoDate(newFrom), to: this.toIsoDate(newTo) };
    this.value.set(range);
    this.confirm.emit(range);
  }

  private toIsoDate(d: Date): string {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
  }

  private clearDisplayInputs(): void {
    this.displayFromInput.set(null);
    this.displayToInput.set(null);
  }

  protected formatFullDate(value: string | null | undefined): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    return `${day}.${month}.${d.getFullYear()}`;
  }
}
